import { z } from 'zod'
import { ApiError } from '@/api/errors'
import type { Adapter } from '@/api'
import type {
  Client,
  ClientWithPending,
  ContentOwner,
  CrawlRun,
  EntityType,
  FacultyMember,
  MediaDetail,
  MediaItem,
  NewClient,
  Program,
  ProgramDetail,
  ProgramPatch,
  ProgramRef,
  Quote,
  QuoteDetail,
  Segment,
  SlateProgram,
  WbProgram,
} from '@/types'
import type { HttpClient } from './client'

/**
 * The Portal API behind the adapter interface.
 *
 * Responses are parsed, not cast: the API and the SPA types were written
 * apart, and a drift (APP-53's integer `id`) should fail here with the field
 * named rather than render `undefined` three components away.
 *
 * Only what the API serves is implemented. Everything else throws a 501
 * rather than falling back to the mock, which is keyed to fabricated client
 * ids and would show a real client empty lists. Each endpoint ticket (APP-54
 * onwards) replaces its stubs here.
 */

const cadence = z.enum(['manual', 'weekly', 'monthly', 'quarterly'])
const pipelineStage = z.enum(['crawled', 'extracted', 'reviewed', 'ready'])

const apiClient = z.object({
  id: z.number().int(),
  name: z.string(),
  primaryDomain: z.string().nullable(),
  additionalDomains: z.array(z.string()),
  includePatterns: z.array(z.string()),
  excludePatterns: z.array(z.string()),
  depthLimit: z.number().int().nullable(),
  pageLimit: z.number().int().nullable(),
  cadence: cadence.nullable(),
  pipelineStage: pipelineStage.nullable(),
  lastCrawlAt: z.string().nullable(),
  lastCrawlRunId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

const apiClientListItem = apiClient.extend({ pendingCount: z.number().int().nullable() })

const count = z.number().int().nullable()

/** A run as `/clients/{id}/crawl-runs` returns it (APP-54), the fields used here. */
const apiCrawlRun = z.object({
  id: z.string(),
  clientId: z.number().int(),
  status: z.enum([
    'requested',
    'submitted',
    'crawling',
    'fetching',
    'preprocessing',
    'extracting',
    'complete',
    'failed',
  ]),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  pagesAttempted: count,
  pagesFetched: count,
  pagesDropped: count,
  pagesFound: count,
  pagesCrawled: count,
  pagesFailed: count,
  pagesSkipped: count,
  failure: z.object({ failedAt: z.string().nullable() }).nullable(),
})

const verdict = z.object({
  at: z.string(),
  by: z.string().nullable(),
  verdict: z.enum(['pending', 'approved', 'rejected']),
  entityHash: z.string(),
})

/** `/clients/{id}/programs` (APP-56). */
const apiProgramRef = z.object({
  id: z.string(),
  name: z.string(),
  wbProgram: z.string(),
  degree: z.string().nullable(),
})

const apiProgram = z.object({
  id: z.string(),
  clientId: z.number().int(),
  name: z.string(),
  wbProgramId: z.string().nullable(),
  wbProgram: z.string().nullable(),
  degree: z.string().nullable(),
  level: z.string().nullable(),
  department: z.string().nullable(),
  description: z.string().nullable(),
  segmentId: z.string().nullable(),
  programTypeId: z.string().nullable(),
  programUrl: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected']),
  origin: z.enum(['extracted', 'manual', 'edited']),
  flagged: z.boolean(),
  favorite: z.boolean(),
  tags: z.array(z.string()),
  entityHash: z.string(),
  verdicts: z.array(verdict),
  slateProgram: z.object({ id: z.string(), slateName: z.string(), enabled: z.boolean() }).nullable(),
  mergedInto: z.object({ id: z.string(), name: z.string() }).nullable(),
})

const apiProgramDetail = apiProgram.extend({
  notes: z.string().nullable(),
  credentialLength: z.string().nullable(),
  cipCode: z.string().nullable(),
  deliveryModes: z.array(z.string()),
  concentrations: z.array(z.string()),
  pages: z.array(
    z.object({ id: z.string(), url: z.string(), title: z.string().nullable(), lastSeenAt: z.string() }),
  ),
  merged: z.array(apiProgramRef),
})

/** `/clients/{id}/quotes` (APP-57). */
const apiQuote = z.object({
  id: z.string(),
  clientId: z.number().int(),
  text: z.string(),
  speakerName: z.string().nullable(),
  classYear: z.string().nullable(),
  majors: z.string().nullable(),
  currentPosition: z.string().nullable(),
  hometown: z.string().nullable(),
  context: z.string().nullable(),
  quoteDate: z.string().nullable(),
  dateProvenance: z.enum(['explicit', 'grad_year', 'url_segment', 'last_modified']).nullable(),
  sourceUrl: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected']),
  origin: z.enum(['extracted', 'manual', 'edited']),
  flagged: z.boolean(),
  favorite: z.boolean(),
  tags: z.array(z.string()),
  entityHash: z.string(),
  verdicts: z.array(verdict),
})

const apiQuoteDetail = apiQuote.extend({
  studentImageUrl: z.string().nullable(),
  majorMinorMentioned: z.string().nullable(),
  notes: z.string().nullable(),
  substance: z.number().int().nullable(),
  programs: z.array(apiProgramRef),
})

/** `/clients/{id}/media` (APP-58). */
const apiMedia = z.object({
  id: z.string(),
  clientId: z.number().int(),
  kind: z.enum(['image', 'video']),
  url: z.string(),
  youtubeId: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  usable: z.boolean(),
  sourceUrl: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected']),
  flagged: z.boolean(),
  favorite: z.boolean(),
  tags: z.array(z.string()),
  entityHash: z.string(),
  verdicts: z.array(verdict),
})

const apiMediaDetail = apiMedia.extend({ programs: z.array(apiProgramRef) })

const apiSlateProgram = z.object({
  id: z.string(),
  clientId: z.number().int(),
  slateName: z.string(),
  parsedName: z.string(),
  parsedDegree: z.string().nullable(),
  enabled: z.boolean(),
  wbProgram: z.object({ id: z.string(), name: z.string() }).nullable(),
  program: apiProgramRef.nullable(),
  mappedPrograms: z.array(apiProgramRef),
})

/** `/clients/{id}/{wb|slate}-programs/{id}/content` (PC-5, PC-9). */
const contentStatus = z.enum(['not_started', 'generating', 'in_review', 'ready'])
const apiContent = z.object({
  id: z.string(),
  headline: z.string().nullable(),
  academicInfo: z.string().nullable(),
  studentFacingValue: z.string().nullable(),
  outcomesPreface: z.string().nullable(),
  outcomes: z.array(z.string()),
  featuredQuoteId: z.string().nullable(),
  imageAssetId: z.string().nullable(),
  featuredQuote: z
    .object({
      id: z.string(),
      text: z.string(),
      studentName: z.string().nullable(),
      classYear: z.string().nullable(),
      majors: z.string().nullable(),
      studentImageUrl: z.string().nullable(),
    })
    .nullable(),
  image: z.object({ url: z.string(), source: z.enum(['override', 'quote']) }).nullable(),
  status: contentStatus,
  readyAt: z.string().nullable(),
  readyBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
const apiProgramContent = z.object({
  owner: z.enum(['wb_program', 'slate_program']),
  ownerId: z.string(),
  own: z.boolean(),
  inheritsFrom: z.object({ id: z.string(), name: z.string() }).nullable(),
  content: apiContent.nullable(),
  status: contentStatus,
  missing: z.array(z.string()),
})
const apiContentSummaries = z.object({
  wbPrograms: z.array(z.object({ id: z.string(), status: contentStatus, missing: z.array(z.string()) })),
  slatePrograms: z.array(
    z.object({ id: z.string(), status: contentStatus, missing: z.array(z.string()), own: z.boolean() }),
  ),
})

/** `/clients/{id}/wb-programs` (PC-33). */
const apiWbProgram = z.object({
  id: z.string(),
  clientId: z.number().int(),
  name: z.string(),
  segmentId: z.string().nullable(),
  slateProgram: z.object({ id: z.string(), slateName: z.string(), enabled: z.boolean() }).nullable(),
  slatePrograms: z.array(z.object({ id: z.string(), slateName: z.string(), enabled: z.boolean() })),
  programCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

const apiFacultyMemberRef = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  imageUrl: z.string().nullable(),
  active: z.boolean(),
})

const apiFacultyMember = apiFacultyMemberRef.extend({
  clientId: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

const apiSegment = z.object({
  id: z.string(),
  clientId: z.number().int(),
  name: z.string(),
  position: z.number().int(),
  headline: z.string().nullable(),
  description: z.string().nullable(),
  featuredQuoteId: z.string().nullable(),
  featuredQuote: z
    .object({
      id: z.string(),
      text: z.string(),
      studentName: z.string().nullable(),
      classYear: z.string().nullable(),
      majors: z.string().nullable(),
      studentImageUrl: z.string().nullable(),
    })
    .nullable(),
  facultyMemberId: z.string().nullable(),
  facultyMember: apiFacultyMemberRef.nullable(),
  imageAssetId: z.string().nullable(),
  imageUrl: z.string().nullable(),
})

/** The fields `PATCH /clients/{id}` accepts. It forbids unknown keys, so send only these. */
const PATCHABLE = [
  'name',
  'primaryDomain',
  'additionalDomains',
  'includePatterns',
  'excludePatterns',
  'depthLimit',
  'pageLimit',
  'cadence',
] as const satisfies ReadonlyArray<keyof Client>

function parse<T>(schema: z.ZodType<T>, body: unknown, what: string): T {
  const parsed = schema.safeParse(body)
  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => `${i.path.join('.')} ${i.message}`).join('; ')
    throw new ApiError(502, `Unexpected ${what} from the Portal API: ${issues}`)
  }
  return parsed.data
}

// Drops the API's extra fields (slug, nickname, location, lastCrawlStatus) until a screen needs them.
function toClient(c: z.infer<typeof apiClient>): Client {
  return {
    id: String(c.id),
    name: c.name,
    primaryDomain: c.primaryDomain,
    additionalDomains: c.additionalDomains,
    includePatterns: c.includePatterns,
    excludePatterns: c.excludePatterns,
    depthLimit: c.depthLimit,
    pageLimit: c.pageLimit,
    cadence: c.cadence,
    pipelineStage: c.pipelineStage,
    lastCrawlAt: c.lastCrawlAt,
    lastCrawlRunId: c.lastCrawlRunId,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  }
}

/**
 * The SPA's three-state `CrawlRun` over the API's eight-state lifecycle.
 * Everything before `complete`/`failed` reads `running`; the stage and the
 * failure reason are dropped until the type is widened (APP-72).
 *
 * The counts fall back from the final counters, which nothing writes yet, to
 * the ones the crawl Lambdas do write: Firecrawl's attempted pages, the pages
 * fetched to S3, and the non-2xx pages dropped into skip records (APP-82).
 */
function toCrawlRun(r: z.infer<typeof apiCrawlRun>): CrawlRun {
  const status = r.status === 'complete' || r.status === 'failed' ? r.status : 'running'
  return {
    id: r.id,
    clientId: String(r.clientId),
    startedAt: r.startedAt,
    finishedAt: r.completedAt ?? (r.status === 'failed' ? (r.failure?.failedAt ?? null) : null),
    status,
    found: r.pagesFound ?? r.pagesAttempted ?? 0,
    crawled: r.pagesCrawled ?? r.pagesFetched ?? 0,
    failed: r.pagesFailed ?? 0,
    skipped: r.pagesSkipped ?? r.pagesDropped ?? 0,
  }
}

/**
 * `previouslyRejected` is false until the API can tell (rejection records are
 * keyed on the old hash, so nothing links a re-entered row to them yet).
 */
function toProgram(p: z.infer<typeof apiProgram>): Program {
  return {
    id: p.id,
    clientId: String(p.clientId),
    name: p.name,
    wbProgramId: p.wbProgramId,
    wbProgram: p.wbProgram,
    degree: p.degree,
    level: p.level,
    department: p.department,
    description: p.description,
    segmentId: p.segmentId,
    programTypeId: p.programTypeId,
    programUrl: p.programUrl,
    sourceUrl: p.sourceUrl,
    slateProgram: p.slateProgram,
    mergedInto: p.mergedInto,
    status: p.status,
    favorite: p.favorite,
    flagged: p.flagged,
    tags: p.tags,
    entityHash: p.entityHash,
    verdicts: p.verdicts.map((v) => ({ ...v, by: v.by ?? 'unknown' })),
    previouslyRejected: false,
    source: p.origin === 'manual' ? 'manual' : 'crawl',
  }
}

function toProgramDetail(p: z.infer<typeof apiProgramDetail>): ProgramDetail {
  return {
    ...toProgram(p),
    notes: p.notes,
    credentialLength: p.credentialLength,
    cipCode: p.cipCode,
    deliveryModes: p.deliveryModes,
    concentrations: p.concentrations,
    pages: p.pages,
    merged: p.merged satisfies ProgramRef[],
  }
}

/** `previouslyRejected` is false for the same reason as on programs. */
function toQuote(q: z.infer<typeof apiQuote>): Quote {
  return {
    id: q.id,
    clientId: String(q.clientId),
    text: q.text,
    speakerName: q.speakerName,
    classYear: q.classYear,
    majors: q.majors,
    currentPosition: q.currentPosition,
    hometown: q.hometown,
    context: q.context,
    quoteDate: q.quoteDate,
    dateProvenance: q.dateProvenance,
    sourceUrl: q.sourceUrl,
    status: q.status,
    favorite: q.favorite,
    flagged: q.flagged,
    tags: q.tags,
    entityHash: q.entityHash,
    verdicts: q.verdicts.map((v) => ({ ...v, by: v.by ?? 'unknown' })),
    previouslyRejected: false,
    source: q.origin === 'manual' ? 'manual' : 'crawl',
  }
}

function toQuoteDetail(q: z.infer<typeof apiQuoteDetail>): QuoteDetail {
  return {
    ...toQuote(q),
    studentImageUrl: q.studentImageUrl,
    majorMinorMentioned: q.majorMinorMentioned,
    notes: q.notes,
    substance: q.substance,
    programs: q.programs satisfies ProgramRef[],
  }
}

/** Every asset is from the crawl until uploads exist (APP-138). */
function toMedia(m: z.infer<typeof apiMedia>): MediaItem {
  return {
    id: m.id,
    clientId: String(m.clientId),
    kind: m.kind,
    url: m.url,
    youtubeId: m.youtubeId,
    title: m.title,
    description: m.description,
    width: m.width,
    height: m.height,
    usable: m.usable,
    sourceUrl: m.sourceUrl,
    status: m.status,
    favorite: m.favorite,
    flagged: m.flagged,
    tags: m.tags,
    entityHash: m.entityHash,
    verdicts: m.verdicts.map((v) => ({ ...v, by: v.by ?? 'unknown' })),
    previouslyRejected: false,
    source: 'crawl',
  }
}

function toMediaDetail(m: z.infer<typeof apiMediaDetail>): MediaDetail {
  return { ...toMedia(m), programs: m.programs satisfies ProgramRef[] }
}

function toSlateProgram(s: z.infer<typeof apiSlateProgram>): SlateProgram {
  return { ...s, clientId: String(s.clientId) }
}

function toWbProgram(w: z.infer<typeof apiWbProgram>): WbProgram {
  return { ...w, clientId: String(w.clientId) }
}

function contentPath(clientId: string, owner: ContentOwner, ownerId: string) {
  const collection = owner === 'wb_program' ? 'wb-programs' : 'slate-programs'
  return `${clientPath(clientId)}/${collection}/${encodeURIComponent(ownerId)}/content`
}

function wbProgramPath(clientId: string, wbProgramId: string) {
  return `${clientPath(clientId)}/wb-programs/${encodeURIComponent(wbProgramId)}`
}

function toSegment(s: z.infer<typeof apiSegment>): Segment {
  return { ...s, clientId: String(s.clientId) }
}

function toFacultyMember(f: z.infer<typeof apiFacultyMember>): FacultyMember {
  return { ...f, clientId: String(f.clientId) }
}

function facultyPath(clientId: string, facultyMemberId: string) {
  return `${clientPath(clientId)}/faculty/${encodeURIComponent(facultyMemberId)}`
}

function clientPath(clientId: string) {
  return `/clients/${encodeURIComponent(clientId)}`
}

function segmentPath(clientId: string, segmentId: string) {
  return `${clientPath(clientId)}/segments/${encodeURIComponent(segmentId)}`
}

function notServed(method: string) {
  return async (): Promise<never> => {
    throw new ApiError(501, `${method} is not served by the Portal API yet`)
  }
}

/**
 * The entities whose review writes the API serves, and their collection path.
 * Facts follow their list endpoint.
 */
const REVIEWED: Partial<Record<EntityType, string>> = { programs: 'programs', quotes: 'quotes', media: 'media' }

function collectionPath(clientId: string, entity: EntityType, method: string) {
  const collection = REVIEWED[entity]
  if (!collection) throw new ApiError(501, `${method} for ${entity} is not served by the Portal API yet`)
  return `${clientPath(clientId)}/${collection}`
}

function programPath(clientId: string, programId: string) {
  return `${clientPath(clientId)}/programs/${encodeURIComponent(programId)}`
}

function quotePath(clientId: string, quoteId: string) {
  return `${clientPath(clientId)}/quotes/${encodeURIComponent(quoteId)}`
}

function mediaPath(clientId: string, assetId: string) {
  return `${clientPath(clientId)}/media/${encodeURIComponent(assetId)}`
}

/**
 * The fields `PATCH /programs/{id}` accepts (APP-130). It forbids unknown keys, so send only these.
 * The WB program, segment and slate mapping are the WB program's (PC-33).
 */
const PROGRAM_PATCHABLE = ['programUrl', 'tags', 'level'] as const satisfies ReadonlyArray<keyof ProgramPatch>

/** The fields `POST /programs` accepts (APP-136, PC-33). `name` and `degree` are the program's identity. */
const PROGRAM_CREATABLE = [
  'name',
  'degree',
  'level',
  'programUrl',
  'tags',
  'wbProgramId',
] as const satisfies ReadonlyArray<keyof ProgramPatch>

/** The fields `PATCH /quotes/{id}` (APP-134) and `POST /quotes` (APP-137) accept. The source page is the crawl's. */
const QUOTE_PATCHABLE = [
  'text',
  'speakerName',
  'classYear',
  'majors',
  'currentPosition',
  'hometown',
  'tags',
] as const satisfies ReadonlyArray<keyof Quote>

/** The fields `PATCH /media/{id}` accepts (APP-58). The URL is the asset's identity. */
const MEDIA_PATCHABLE = ['title', 'description', 'tags'] as const satisfies ReadonlyArray<keyof MediaItem>

/** Only `keys` of `patch`: the API forbids unknown ones. */
function pick<T extends object>(patch: T, keys: ReadonlyArray<keyof T>) {
  return Object.fromEntries(keys.filter((k) => k in patch).map((k) => [k, patch[k]]))
}

export function createHttpAdapter(http: HttpClient): Adapter {
  return {
    /* -------------------------------------------------- clients (APP-53) */

    async listClients(): Promise<ClientWithPending[]> {
      const body = parse(z.array(apiClientListItem), await http.get('/clients'), 'client list')
      return body.map((c) => ({ ...toClient(c), pendingCount: c.pendingCount }))
    },

    async createClient(client: NewClient) {
      // 409 on a duplicate id or slug, 422 on a bad domain; both arrive as
      // ApiError with the API's message. A null cadence is omitted, not sent.
      const { cadence, ...rest } = client
      const body = cadence ? { ...rest, cadence } : rest
      return toClient(parse(apiClient, await http.post('/clients', body), 'client'))
    },

    async getClient(clientId) {
      return toClient(parse(apiClient, await http.get(clientPath(clientId)), 'client'))
    },

    async updateClient(clientId, patch) {
      const body = Object.fromEntries(PATCHABLE.filter((k) => k in patch).map((k) => [k, patch[k]]))
      return toClient(parse(apiClient, await http.patch(clientPath(clientId), body), 'client'))
    },

    /* ----------------------------------------------- crawl runs (APP-54) */

    async getLatestCrawlRun(clientId) {
      const runs = parse(
        z.array(apiCrawlRun),
        await http.get(`${clientPath(clientId)}/crawl-runs?limit=1`),
        'crawl run list',
      )
      const [latest] = runs
      return latest ? toCrawlRun(latest) : null
    },

    async startCrawl(clientId) {
      // An empty body crawls the primary domain. 409 while a run is unfinished,
      // 422 without a page limit; both arrive as ApiError with the API's message.
      return toCrawlRun(parse(apiCrawlRun, await http.post(`${clientPath(clientId)}/crawl-runs`, {}), 'crawl run'))
    },

    /* ------------------------------------------------- programs (APP-56) */

    async listPrograms(clientId) {
      const body = parse(z.array(apiProgram), await http.get(`${clientPath(clientId)}/programs`), 'program list')
      return body.map(toProgram)
    },

    async getProgram(clientId, programId) {
      return toProgramDetail(parse(apiProgramDetail, await http.get(programPath(clientId, programId)), 'program'))
    },

    /* ------------------------------------- program edits, verdicts (APP-130, APP-55) */

    async upsertProgram(clientId, patch) {
      // A create is approved by its author. 409 when the name and degree already have a row.
      const response = patch.id
        ? await http.patch(programPath(clientId, patch.id), pick(patch, PROGRAM_PATCHABLE))
        : await http.post(`${clientPath(clientId)}/programs`, pick(patch, PROGRAM_CREATABLE))
      return toProgramDetail(parse(apiProgramDetail, response, 'program'))
    },

    async setStatus(clientId, entity, ids, status) {
      // All or nothing: one unknown id is a 404 and nothing changes.
      await http.post(`${collectionPath(clientId, entity, 'setStatus')}/review`, { ids, status })
    },

    async setMark(clientId, entity, id, kind, on) {
      const path = `${collectionPath(clientId, entity, 'setMark')}/${encodeURIComponent(id)}/marks/${kind}`
      await (on ? http.put(path) : http.delete(path))
    },

    async setTags(clientId, entity, ids, tags) {
      // One PATCH per row, each its own transaction. The drawer sends one.
      const collection = collectionPath(clientId, entity, 'setTags')
      await Promise.all(ids.map((id) => http.patch(`${collection}/${encodeURIComponent(id)}`, { tags })))
    },

    async addTagToMany(clientId, entity, ids, tag) {
      await http.post(`${collectionPath(clientId, entity, 'addTagToMany')}/tags`, { ids, tag })
    },

    /* ------------------------------------------------- WB programs (PC-33) */

    async listWbPrograms(clientId) {
      const body = parse(z.array(apiWbProgram), await http.get(`${clientPath(clientId)}/wb-programs`), 'WB program list')
      return body.map(toWbProgram)
    },

    async createWbProgram(clientId, input) {
      // 409 when the client already has the name, ignoring case, punctuation and &/and.
      const body = await http.post(`${clientPath(clientId)}/wb-programs`, input)
      return toWbProgram(parse(apiWbProgram, body, 'WB program'))
    },

    async updateWbProgram(clientId, wbProgramId, patch) {
      const body = await http.patch(wbProgramPath(clientId, wbProgramId), patch)
      return toWbProgram(parse(apiWbProgram, body, 'WB program'))
    },

    async deleteWbProgram(clientId, wbProgramId) {
      // Its crawled programs become unassigned; its slate programs unmapped.
      await http.delete(wbProgramPath(clientId, wbProgramId))
    },

    async setWbProgramSegment(clientId, ids, segmentId) {
      // All or nothing, as setStatus. Null unassigns.
      await http.post(`${clientPath(clientId)}/wb-programs/segment`, { ids, segmentId })
    },

    /* ----------------------------------------- program content (PC-5, PC-9) */

    async getProgramContent(clientId, owner, ownerId) {
      return parse(apiProgramContent, await http.get(contentPath(clientId, owner, ownerId)), 'program content')
    },

    async updateProgramContent(clientId, owner, ownerId, patch) {
      // A slate program without its own record is a 422: create it first.
      const body = await http.patch(contentPath(clientId, owner, ownerId), patch)
      return parse(apiProgramContent, body, 'program content')
    },

    async setProgramContentStatus(clientId, owner, ownerId, status, override) {
      // Ready with required content missing is a 409 unless override.
      const body = await http.post(`${contentPath(clientId, owner, ownerId)}/status`, { status, override })
      return parse(apiProgramContent, body, 'program content')
    },

    async createSlateContent(clientId, slateProgramId, copyFromWbProgram) {
      const body = await http.post(contentPath(clientId, 'slate_program', slateProgramId), { copyFromWbProgram })
      return parse(apiProgramContent, body, 'program content')
    },

    async deleteSlateContent(clientId, slateProgramId) {
      await http.delete(contentPath(clientId, 'slate_program', slateProgramId))
    },

    async listContentSummaries(clientId) {
      const body = await http.get(`${clientPath(clientId)}/program-content`)
      return parse(apiContentSummaries, body, 'program content summaries')
    },

    async assignPrograms(clientId, ids, target) {
      // A name is found, or created when new. Null leaves them unassigned.
      await http.post(`${clientPath(clientId)}/programs/wb-program`, { ids, ...(target ?? {}) })
    },

    async listSlatePrograms(clientId) {
      const path = `${clientPath(clientId)}/slate-programs`
      return parse(z.array(apiSlateProgram), await http.get(path), 'slate program list').map(toSlateProgram)
    },

    async mapSlateProgram(clientId, slateProgramId, wbProgramId) {
      const path = `${clientPath(clientId)}/slate-programs/${encodeURIComponent(slateProgramId)}`
      return toSlateProgram(parse(apiSlateProgram, await http.patch(path, { wbProgramId }), 'slate program'))
    },

    async listSegments(clientId) {
      const body = parse(z.array(apiSegment), await http.get(`${clientPath(clientId)}/segments`), 'segment list')
      return body.map(toSegment)
    },

    /* ------------------------------------------- segment edits (PC-19, PC-20) */

    async listDefaultSegments(clientId) {
      return parse(z.array(z.string()), await http.get(`${clientPath(clientId)}/segments/defaults`), 'default segments')
    },

    async listSuggestedPrograms(clientId) {
      const schema = z.array(z.object({ name: z.string(), variations: z.array(z.string()) }))
      return parse(schema, await http.get(`${clientPath(clientId)}/programs/suggestions`), 'suggested programs')
    },

    async createSegment(clientId, name) {
      // 409 when the client already has the name, ignoring case.
      const body = await http.post(`${clientPath(clientId)}/segments`, { name })
      return toSegment(parse(apiSegment, body, 'segment'))
    },

    async renameSegment(clientId, segmentId, name) {
      return toSegment(parse(apiSegment, await http.patch(segmentPath(clientId, segmentId), { name }), 'segment'))
    },

    async updateSegment(clientId, segmentId, patch) {
      // 422 when a newly picked quote is not approved or a faculty member is inactive.
      return toSegment(
        parse(apiSegment, await http.patch(segmentPath(clientId, segmentId), patch), 'segment'),
      )
    },

    async moveSegment(clientId, segmentId, direction) {
      const body = await http.post(`${segmentPath(clientId, segmentId)}/move`, { direction })
      return parse(z.array(apiSegment), body, 'segment list').map(toSegment)
    },

    async deleteSegment(clientId, segmentId, reassignTo) {
      // No reassignTo leaves the segment's programs unassigned.
      const query = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ''
      await http.delete(`${segmentPath(clientId, segmentId)}${query}`)
    },

    /* ------------------------------------------------- faculty members (PC-24) */

    async listFaculty(clientId) {
      const body = parse(
        z.array(apiFacultyMember),
        await http.get(`${clientPath(clientId)}/faculty`),
        'faculty list',
      )
      return body.map(toFacultyMember)
    },

    async createFacultyMember(clientId, input) {
      // 409 when another faculty member of the client has the email, ignoring case.
      const body = await http.post(`${clientPath(clientId)}/faculty`, input)
      return toFacultyMember(parse(apiFacultyMember, body, 'faculty member'))
    },

    async updateFacultyMember(clientId, facultyMemberId, patch) {
      const body = await http.patch(facultyPath(clientId, facultyMemberId), patch)
      return toFacultyMember(parse(apiFacultyMember, body, 'faculty member'))
    },

    /* --------------------------------------------- quotes (APP-57, APP-134) */

    async listQuotes(clientId) {
      const body = parse(z.array(apiQuote), await http.get(`${clientPath(clientId)}/quotes`), 'quote list')
      return body.map(toQuote)
    },

    async getQuote(clientId, quoteId) {
      return toQuoteDetail(parse(apiQuoteDetail, await http.get(quotePath(clientId, quoteId)), 'quote'))
    },

    async upsertQuote(clientId, patch) {
      // As for programs; 409 when the text already has a row.
      const response = patch.id
        ? await http.patch(quotePath(clientId, patch.id), pick(patch, QUOTE_PATCHABLE))
        : await http.post(`${clientPath(clientId)}/quotes`, pick(patch, QUOTE_PATCHABLE))
      return toQuoteDetail(parse(apiQuoteDetail, response, 'quote'))
    },

    /* ---------------------------------------------------- media (APP-58) */

    async listMedia(clientId) {
      const body = parse(z.array(apiMedia), await http.get(`${clientPath(clientId)}/media`), 'media list')
      return body.map(toMedia)
    },

    async getMedia(clientId, assetId) {
      return toMediaDetail(parse(apiMediaDetail, await http.get(mediaPath(clientId, assetId)), 'media asset'))
    },

    async upsertMedia(clientId, patch) {
      // No create: adding media is an upload, which waits on storage (APP-138).
      if (!patch.id) throw new ApiError(501, 'Uploading media is not served by the Portal API yet')
      const response = await http.patch(mediaPath(clientId, patch.id), pick(patch, MEDIA_PATCHABLE))
      return toMediaDetail(parse(apiMediaDetail, response, 'media asset'))
    },

    /* ------------------------------------------------- not served yet */

    getReviewSummary: notServed('getReviewSummary'),
    listClientTags: notServed('listClientTags'),
    listPages: notServed('listPages'),
    excludePage: notServed('excludePage'),
    setPageImportance: notServed('setPageImportance'),
    retryPage: notServed('retryPage'),
    listProgramTypes: notServed('listProgramTypes'),
    listFacts: notServed('listFacts'),
    deleteEntities: notServed('deleteEntities'),
    upsertFact: notServed('upsertFact'),
    listCopyDocs: notServed('listCopyDocs'),
    upsertCopyDoc: notServed('upsertCopyDoc'),
    setCopyFavorite: notServed('setCopyFavorite'),
    deleteCopyDoc: notServed('deleteCopyDoc'),
  }
}
